import {
  ExternalChannelAccessResponse,
  LoginResponse,
} from '../models/login.model';
import { User, UserByUuidResponse } from '../models/user.model';
import { AuthService } from '../services/auth.service';
import { UserFacade } from '../store/user/user.facade';
import { STORAGE_MCE_REDIRECT_PRODUCT_INTENT } from '../constants/mce-redirect-products.constants';

import { unblockCartHydration } from './cart-hydration.util';
import { unformatCnpj } from './cnpj.util';

export const STORAGE_EXTERNAL_CHANNEL_ACCESS = 'external_channel_access';
export const STORAGE_EXTERNAL_CHANNEL_ORIGIN = 'external_channel_origin';
export const STORAGE_EXTERNAL_CHANNEL_CAMPAIGN = 'external_channel_campaign';
export const STORAGE_REDIRECT_AFTER_LOGIN = 'redirect_after_login';
export const STORAGE_PENDING_ORDERS_TOKEN = 'mce_pending_order_token';

export function normalizeExternalChannelOrigin(
  value: string | null | undefined
): string {
  return (value ?? '').trim().toUpperCase();
}

export function getExternalChannelOrigin(
  data: ExternalChannelAccessResponse | null | undefined,
  fallbackOrigin?: string | null
): string {
  const fromUrl = normalizeExternalChannelOrigin(fallbackOrigin);
  if (fromUrl) {
    return fromUrl;
  }

  const userRequest = data?.userRequest;
  return normalizeExternalChannelOrigin(
    userRequest?.origin ||
      userRequest?.originChannel ||
      userRequest?.accessChannel?.name
  );
}

export function isMceExternalChannelFlow(
  data: ExternalChannelAccessResponse | null | undefined,
  fallbackOrigin?: string | null
): boolean {
  return getExternalChannelOrigin(data, fallbackOrigin) === 'CLARO_MCE';
}

export function withPersistedExternalChannelOrigin(
  response: ExternalChannelAccessResponse,
  requestOrigin: string
): ExternalChannelAccessResponse {
  const normalizedOrigin = normalizeExternalChannelOrigin(requestOrigin);
  if (!normalizedOrigin) {
    return response;
  }

  return {
    ...response,
    userRequest: {
      ...response.userRequest,
      origin: normalizedOrigin,
      originChannel: normalizedOrigin,
    },
  };
}

export function readStoredExternalChannelOrigin(): string {
  if (typeof sessionStorage === 'undefined') {
    return '';
  }

  try {
    return normalizeExternalChannelOrigin(
      sessionStorage.getItem(STORAGE_EXTERNAL_CHANNEL_ORIGIN)
    );
  } catch {
    return '';
  }
}

export function persistExternalChannelCampaign(
  value: string | null | undefined
): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }

  const campaign = (value ?? '').trim();
  if (!campaign) {
    return;
  }

  try {
    sessionStorage.setItem(STORAGE_EXTERNAL_CHANNEL_CAMPAIGN, campaign);
  } catch {
    // ignore
  }
}

export function clearStoredExternalChannelCampaign(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }

  try {
    sessionStorage.removeItem(STORAGE_EXTERNAL_CHANNEL_CAMPAIGN);
  } catch {
    // ignore
  }
}

export function readStoredExternalChannelCampaign(): string {
  if (typeof sessionStorage === 'undefined') {
    return '';
  }

  try {
    return (sessionStorage.getItem(STORAGE_EXTERNAL_CHANNEL_CAMPAIGN) ?? '').trim();
  } catch {
    return '';
  }
}

/** `completeAddress: true` significa que o endereço já está completo. */
export function isCompleteAddress(
  value: string | boolean | null | undefined
): boolean {
  if (value === true) {
    return true;
  }

  if (value === false) {
    return false;
  }

  return (
    String(value ?? '')
      .trim()
      .toLowerCase() === 'true'
  );
}

/** Precisa da tela de complete-address quando o flag é false/ausente. */
export function needsAddressCompletion(
  value: string | boolean | null | undefined
): boolean {
  return !isCompleteAddress(value);
}

export function shouldCompleteAddressFirst(
  response: ExternalChannelAccessResponse | null | undefined
): boolean {
  return !!response && needsAddressCompletion(response.completeAddress);
}

/** SUE = móvel; NETSMS = fixa; COMBO_SUE_NETSMS = escolha de segmento. */
export function isExternalChannelComboType(
  type: string | null | undefined
): boolean {
  return type === 'COMBO_SUE_NETSMS';
}

export function isExternalChannelMobileType(
  type: string | null | undefined
): boolean {
  return type === 'SUE';
}

export function isExternalChannelFixaType(
  type: string | null | undefined
): boolean {
  return type === 'NETSMS';
}

export function getExternalChannelMobileDocument(
  response: ExternalChannelAccessResponse | null | undefined
): string {
  return unformatCnpj(response?.userRequest?.document || '');
}

export function getPostAddressCompletionRoute(
  response: ExternalChannelAccessResponse,
  safeRedirect?: string | null
): string {
  if (isExternalChannelComboType(response.type)) {
    return '/segment-selection';
  }

  if (isExternalChannelFixaType(response.type)) {
    return '/select-fixa-contract';
  }

  const redirect = safeRedirect?.trim();
  if (redirect) {
    return redirect.startsWith('/') ? redirect : `/${redirect}`;
  }

  return '/';
}

export function readExternalChannelAccessFromSession(): ExternalChannelAccessResponse | null {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }

  try {
    const raw = sessionStorage.getItem(STORAGE_EXTERNAL_CHANNEL_ACCESS);
    if (!raw) {
      return null;
    }

    return JSON.parse(raw) as ExternalChannelAccessResponse;
  } catch {
    return null;
  }
}

export function clearExternalChannelSessionStorage(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }

  try {
    sessionStorage.removeItem(STORAGE_EXTERNAL_CHANNEL_ACCESS);
    sessionStorage.removeItem(STORAGE_EXTERNAL_CHANNEL_ORIGIN);
    sessionStorage.removeItem(STORAGE_REDIRECT_AFTER_LOGIN);
    sessionStorage.removeItem(STORAGE_MCE_REDIRECT_PRODUCT_INTENT);
    unblockCartHydration();
  } catch {
    // ignore
  }
}

export function validateTokenUUID(value: string): boolean {
  if (!value || typeof value !== 'string') return false;
  const trimmed = value.trim();
  return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(trimmed);
}

export function persistPendingOrdersToken(value: string | null | undefined): void {
  if (typeof sessionStorage === 'undefined') return;
  const token = (value ?? '').trim();
  if (!token || !validateTokenUUID(token)) return;
  try {
    sessionStorage.setItem(STORAGE_PENDING_ORDERS_TOKEN, token);
  } catch { /* ignore */ }
}

export function readPendingOrdersToken(): string | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const token = sessionStorage.getItem(STORAGE_PENDING_ORDERS_TOKEN);
    return token || null;
  } catch { return null; }
}

export function clearPendingOrdersToken(): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(STORAGE_PENDING_ORDERS_TOKEN);
  } catch { /* ignore */ }
}

export interface ExternalChannelFinalRedirect {
  commands: string[];
  queryParams?: Record<string, string>;
}

/**
 * Constrói o redirect final, incorporando o token de pedidos pendentes se aplicável.
 * - Lê STORAGE_REDIRECT_AFTER_LOGIN; retorna null se ausente.
 * - Se o caminho é /my-account/pending-orders:
 *   - Se o token existe: retorna complete + queryParams.token e limpa o token.
 *   - Se não há token: retorna a lista de pendentes.
 * - Caso contrário, retorna o caminho como está.
 * - Sempre limpa STORAGE_REDIRECT_AFTER_LOGIN após a leitura.
 */
export function constructFinalRedirectWithToken(): ExternalChannelFinalRedirect | null {
  if (typeof sessionStorage === 'undefined') return null;

  try {
    const redirect = sessionStorage.getItem(STORAGE_REDIRECT_AFTER_LOGIN);
    // Sempre limpa o redirect após a leitura (one-shot)
    sessionStorage.removeItem(STORAGE_REDIRECT_AFTER_LOGIN);

    if (!redirect || !redirect.trim()) {
      return null;
    }

    const path = redirect.trim();

    if (path === '/my-account/pending-orders') {
      const token = readPendingOrdersToken();
      if (token) {
        clearPendingOrdersToken();
        return {
          commands: ['/my-account/pending-orders/complete'],
          queryParams: { token },
        };
      }
      return { commands: ['/my-account/pending-orders'] };
    }

    return { commands: [path] };
  } catch {
    return null;
  }
}

export function mapExternalChannelUser(
  response: ExternalChannelAccessResponse
): User {
  const u = response.userRequest;
  return {
    uuid: u.uuid,
    firstName: u.firstName,
    lastName: u.lastName,
    displayName: u.displayName,
    email: u.email,
    login: u.login,
    document: u.document,
    documentType: (u.documentType ?? '') as string,
    role: u.role,
    business: u.business,
    phone: u.phone,
    addresses: (u.addresses || []).map(a => ({
      uuid: (a.uuid ?? '') as string,
      address: a.address,
      neighborhood: a.neighborhood,
      city: a.city,
      state: a.state,
      country: a.country,
      zipCode: a.zipCode,
      complement: a.complement,
      main: a.main,
      type: a.type,
      number: a.number,
    })),
    terms: [],
    confirmationUrl: u.confirmationUrl ?? undefined,
    origin: u.origin ?? undefined,
    accessChannel: u.accessChannel?.name ?? undefined,
    userType: u.userType ?? undefined,
  };
}

export function resolveExternalChannelResources(
  response: ExternalChannelAccessResponse
): string[] | ExternalChannelAccessResponse['contracts'] {
  // SUE = login móvel: resources são CNPJs (strings)
  if (isExternalChannelMobileType(response.type)) {
    const document = getExternalChannelMobileDocument(response);
    return document ? [document] : [];
  }

  // NETSMS (fixa) e COMBO: resources são contratos (objetos)
  return Array.isArray(response.contracts) ? response.contracts : [];
}

export function buildExternalChannelLoginResponse(
  response: ExternalChannelAccessResponse
): LoginResponse {
  const user = mapExternalChannelUser(response);

  return {
    status: 'success',
    msg: 'Login realizado com sucesso',
    error: false,
    errors: null,
    page: null,
    nextPage: null,
    lastPage: null,
    nextPageUrl: null,
    data: {
      loginAccess: user.email || user.displayName || user.document,
      loginDate: new Date().toISOString(),
      userUuid: user.uuid,
      isFirstAccess: response.firstAccess,
      resources: resolveExternalChannelResources(response),
      document: user.document,
    },
  };
}

/**
 * Publica login + currentUser e persiste LoginData de forma consistente.
 * Usado após acesso direto ou conclusão do endereço no canal externo.
 */
export function hydrateExternalChannelSession(
  response: ExternalChannelAccessResponse,
  authService: AuthService,
  userFacade: UserFacade,
  persist = true
): { loginResponse: LoginResponse; user: User } {
  const loginResponse = buildExternalChannelLoginResponse(response);
  const user = mapExternalChannelUser(response);

  userFacade.loginSuccess(loginResponse);
  userFacade.loadUserDetailsSuccess({
    status: 'success',
    data: user,
  } as UserByUuidResponse);

  if (persist) {
    authService.persistLoginData(loginResponse.data);
  }

  return { loginResponse, user };
}
